/**
 * Helpers for tenant rent evidence: rejected state, resubmit context (Payment History + Pay modal).
 */

export function getTenantAssignmentId(rental) {
  if (!rental || typeof rental !== 'object') return null;
  return (
    rental.assignmentId ??
    rental.tenantAssignmentId ??
    rental.unitAssignmentId ??
    rental.assignment?.id ??
    null
  );
}

function periodMatchesScoped(py, pm, year, month) {
  if (py == null || pm == null) return true;
  return Number(py) === Number(year) && Number(pm) === Number(month);
}

function periodMatchesRow(py, pm, year, month) {
  if (py == null || pm == null) return false;
  return Number(py) === Number(year) && Number(pm) === Number(month);
}

function rejectedRentEvidenceFromObject(ev, year, month) {
  if (!ev || typeof ev !== 'object') return null;
  const st = String(ev.status || ev.evidenceStatus || '').toUpperCase();
  if (st !== 'REJECTED') return null;
  const py = ev.year ?? ev.periodYear;
  const pm = ev.month ?? ev.periodMonth;
  if (!periodMatchesScoped(py, pm, year, month)) return null;
  return {
    rejectionReason: ev.rejectionReason || ev.reason || '',
    prefill: {
      amountPaid: ev.amountPaid != null ? String(ev.amountPaid) : '',
      mpesaMessage: typeof ev.mpesaMessage === 'string' ? ev.mpesaMessage : '',
      photoUrl: typeof ev.photoUrl === 'string' ? ev.photoUrl : '',
    },
  };
}

/**
 * When viewing a specific month in Payment History (and payment rows / meta from API).
 */
export function getRentEvidenceResubmitContext(rental, year, month, paymentRows, historyMeta) {
  const assignmentId = getTenantAssignmentId(rental);
  if (!assignmentId) {
    return { show: false, assignmentId: null, rejectionReason: '', prefill: {} };
  }

  const fromMeta = rejectedRentEvidenceFromObject(historyMeta, year, month);
  if (fromMeta) {
    return { show: true, assignmentId, ...fromMeta };
  }

  const nestedSources = [
    rental?.rentEvidence,
    rental?.monthlyRentEvidence,
    rental?.currentRentEvidence,
    rental?.periodRentEvidence,
  ];
  for (const ev of nestedSources) {
    const hit = rejectedRentEvidenceFromObject(ev, year, month);
    if (hit) return { show: true, assignmentId, ...hit };
  }

  const flatSt = String(rental?.rentEvidenceStatus || rental?.monthlyRentEvidenceStatus || '').toUpperCase();
  if (flatSt === 'REJECTED') {
    const py = rental?.rentEvidenceYear ?? rental?.evidenceYear ?? rental?.periodYear;
    const pm = rental?.rentEvidenceMonth ?? rental?.evidenceMonth ?? rental?.periodMonth;
    if (periodMatchesScoped(py, pm, year, month)) {
      return {
        show: true,
        assignmentId,
        rejectionReason: rental?.rentEvidenceRejectionReason || rental?.rejectionReason || '',
        prefill: {
          amountPaid:
            rental?.rentEvidenceAmountPaid != null ? String(rental.rentEvidenceAmountPaid) : '',
          mpesaMessage: rental?.rentEvidenceMpesaMessage || '',
          photoUrl: rental?.rentEvidencePhotoUrl || '',
        },
      };
    }
  }

  const rows = Array.isArray(paymentRows) ? paymentRows : [];
  for (const r of rows) {
    const nested = r.rentEvidence && typeof r.rentEvidence === 'object' ? r.rentEvidence : null;
    const st = String(
      r.rentEvidenceStatus || r.evidenceStatus || nested?.status || nested?.evidenceStatus || ''
    ).toUpperCase();
    if (st !== 'REJECTED') continue;
    const py = r.periodYear ?? nested?.year ?? nested?.periodYear ?? r.year;
    const pm = r.periodMonth ?? nested?.month ?? nested?.periodMonth ?? r.month;
    if (!periodMatchesRow(py, pm, year, month)) continue;
    return {
      show: true,
      assignmentId,
      rejectionReason:
        r.rentEvidenceRejectionReason ||
        r.rejectionReason ||
        nested?.rejectionReason ||
        nested?.reason ||
        '',
      prefill: {
        amountPaid:
          r.amountPaid != null
            ? String(r.amountPaid)
            : nested?.amountPaid != null
              ? String(nested.amountPaid)
              : '',
        mpesaMessage: r.mpesaMessage || nested?.mpesaMessage || '',
        photoUrl: r.photoUrl || nested?.photoUrl || '',
      },
    };
  }

  return { show: false, assignmentId, rejectionReason: '', prefill: {} };
}

/**
 * Any rejected rent evidence on the current lease (from GET /api/rental/status), for Home + Pay modal.
 */
export function getRejectedRentEvidenceFromRental(rental) {
  const assignmentId = getTenantAssignmentId(rental);
  if (!assignmentId) return null;

  const nestedSources = [
    rental?.rentEvidence,
    rental?.monthlyRentEvidence,
    rental?.currentRentEvidence,
    rental?.periodRentEvidence,
  ];
  for (const ev of nestedSources) {
    if (!ev || typeof ev !== 'object') continue;
    const st = String(ev.status || ev.evidenceStatus || '').toUpperCase();
    if (st !== 'REJECTED') continue;
    const py = ev.year ?? ev.periodYear;
    const pm = ev.month ?? ev.periodMonth;
    return {
      assignmentId,
      periodYear: py != null ? Number(py) : null,
      periodMonth: pm != null ? Number(pm) : null,
      rejectionReason: ev.rejectionReason || ev.reason || '',
      prefill: {
        amountPaid: ev.amountPaid != null ? String(ev.amountPaid) : '',
        mpesaMessage: typeof ev.mpesaMessage === 'string' ? ev.mpesaMessage : '',
        photoUrl: typeof ev.photoUrl === 'string' ? ev.photoUrl : '',
      },
    };
  }

  const flatSt = String(rental?.rentEvidenceStatus || rental?.monthlyRentEvidenceStatus || '').toUpperCase();
  if (flatSt === 'REJECTED') {
    const py = rental?.rentEvidenceYear ?? rental?.evidenceYear ?? rental?.periodYear;
    const pm = rental?.rentEvidenceMonth ?? rental?.evidenceMonth ?? rental?.periodMonth;
    return {
      assignmentId,
      periodYear: py != null ? Number(py) : null,
      periodMonth: pm != null ? Number(pm) : null,
      rejectionReason: rental?.rentEvidenceRejectionReason || rental?.rejectionReason || '',
      prefill: {
        amountPaid:
          rental?.rentEvidenceAmountPaid != null ? String(rental.rentEvidenceAmountPaid) : '',
        mpesaMessage: rental?.rentEvidenceMpesaMessage || '',
        photoUrl: rental?.rentEvidencePhotoUrl || '',
      },
    };
  }

  return null;
}
